import React, { useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Account } from '@tcs/client-portal-api/types/Account';
import { SelectorItem } from '@tcs/common/components/atoms/Selector';
import TitleSelector from '@tcs/common/components/molecules/TitleSelector';
import Spinner from '@tcs/common/components/atoms/Spinner';
import { AccountsWithNameHeader } from '@tcs/client-portal-common/components/organisms/Header/AccountsWithNameHeader';

import Header, { HeaderProps } from '.';
import { useStyles } from './style';

const ALL_ACCOUNTS_VALUE = 'AllAccounts';

type IndividualAccountsHeaderProps = Omit<HeaderProps, 'pageTitle'> & {
  accounts: Account[];
  loading: boolean;
};

const IndividualAccountsHeader = ({
  accounts,
  loading,
  ...props
}: IndividualAccountsHeaderProps) => {
  const classes = useStyles();
  const nav = useNavigate();
  const location = useLocation();
  const params = useParams<{ accountNumber: string }>();

  useEffect(() => {
    if (
      accounts &&
      accounts.length === 1 &&
      location.pathname.startsWith('/accounts/all')
    ) {
      const account = accounts[0];
      nav(`/accounts/${account.accountNumber}`, { replace: true });
    }
  }, [nav, location, accounts]);

  const listItem = (
    accountNumber: string,
    displayAccountType: string,
    status: string
  ) => {
    return (
      <div className={classes.itemInFunction}>
        <div className={classes.displayAccountType}>
          {displayAccountType ? displayAccountType : ''}
        </div>
        <div className={classes.accountNumber}>
          {accountNumber ? accountNumber : ''}
        </div>
        {status && (status === 'Closed' || status === 'Void') && (
          <div className={classes.status}>{status ? 'Closed' : ''}</div>
        )}
      </div>
    );
  };

  const selectorItems: SelectorItem[] = useMemo(() => {
    if (accounts == null) {
      return [];
    }
    const accountItems = accounts.map(
      ({ accountNumber, displayAccountType, status }: Account) => ({
        value: accountNumber,
        listItem: listItem(accountNumber, displayAccountType, status),
      })
    );
    if (accountItems.length === 1) {
      return accountItems;
    }
    return [
      {
        value: ALL_ACCOUNTS_VALUE,
        listItem: <div>All Accounts</div>,
      },
      ...accountItems,
    ];
  }, [accounts]);

  const onSelectAccount = useCallback(
    (accountNumber: string) => {
      // replace only the account segment, leaving the rest intact
      const accountSegment =
        accountNumber === ALL_ACCOUNTS_VALUE ? 'all' : accountNumber;
      const path = location.pathname.replace(
        /\/accounts\/[^/]+/,
        `/accounts/${accountSegment}`
      );
      nav(path);
    },
    [nav, location]
  );

  if (loading) {
    return <Spinner />;
  }

  const selectedValue = params.accountNumber || ALL_ACCOUNTS_VALUE;

  if (!selectorItems.some((item) => item.value === selectedValue)) {
    return <Spinner />;
  }

  // use the AccountsWithNameHeader to show a banner if account is closed/void
  // use regular header for the individual account banner/info
  return (
    <>
      <AccountsWithNameHeader
        accounts={accounts}
        loading={loading}
        pageTitle={''}
      />
      <Header {...props}>
        <div className={classes.accountsHeader}>
          <TitleSelector
            items={selectorItems}
            selectedValue={selectedValue}
            onSelect={onSelectAccount}
          />
        </div>
      </Header>
    </>
  );
};

export default IndividualAccountsHeader;
